import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { Modal } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import GeoLocationComponent from 'components/GeoLocationComponent/GeoLocationComponent';
import { useEffect, useMemo, useState } from 'react';
import generateUUID from 'utils/sharedUtils/generateUUID';
import { useAors, useUpsertAor } from 'hooks/useAors';
import { AreaOfResponsibility } from '../../types/aor';
import { validateGeoLocationSize } from 'utils/validateGeoLocationSize';
import { GeoLocation } from '../../components/GeoLocationComponent/GeoLocationComponent';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import { buildAorParentOptions } from './utils/aorParentOptions';
import { useAorText } from 'utils/aorTerminology';

interface FormData {
  name: string;
  description: string;
  parent_item_id: string | null;
}

type ParentOption = { label: string; value: string };

interface AddUpdateAorProps {
  show: boolean;
  onClose: () => void;
  mode: 'Add' | 'Edit';
  editAorData: AreaOfResponsibility | null;
}

export const AddUpdateAor: React.FC<AddUpdateAorProps> = ({
  show,
  onClose,
  mode,
  editAorData,
}) => {
  const aorText = useAorText();
  const [geoLocation, setGeoLocation] = useState<GeoLocation | undefined>(
    undefined
  );
  const [originalLocationString, setOriginalLocationString] = useState<
    string | null
  >(null);
  const upsertAor = useUpsertAor();
  const { data: aors = [] } = useAors();
  const parentOptions = useMemo<ParentOption[]>(
    () => buildAorParentOptions(aors, editAorData?.id ?? null),
    [aors, editAorData?.id]
  );

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      description: '',
      parent_item_id: null,
    },
  });

  useEffect(() => {
    if (show && mode === 'Add') {
      reset({
        name: '',
        description: '',
        parent_item_id: null,
      });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setGeoLocation(undefined);
      setOriginalLocationString(null);
    } else if (show && mode === 'Edit' && editAorData) {
      setValue('name', editAorData.name);
      setValue('description', editAorData.description || '');
      setValue('parent_item_id', editAorData.parent_item_id ?? null);
      const loc = editAorData.geolocation || undefined;
      setGeoLocation(loc);
      const locString = loc ? JSON.stringify(loc) : null;
      setOriginalLocationString(locString);
    }
  }, [show, mode, reset, editAorData, setValue]);

  const onSubmit = (data: FormData) => {
    // Check if location data has changed by comparing stringified versions
    const currentLocationString = geoLocation
      ? JSON.stringify(geoLocation)
      : null;
    const locationChanged =
      mode === 'Add' || currentLocationString !== originalLocationString;

    let location: string | null | undefined;

    // Only validate and update location if it has changed
    if (locationChanged) {
      const { isValid, locationString } = validateGeoLocationSize(geoLocation);
      location = isValid ? locationString : null;
    } else {
      location = undefined;
    }

    const aor: AreaOfResponsibility = {
      id: editAorData?.id || generateUUID(),
      group_guid: editAorData?.group_guid || '',
      name: data.name,
      description: data.description,
      location,
      category_id: null,
      parent_item_id: data.parent_item_id || null,
      cc_type: null,
    };
    upsertAor.mutate(aor, {
      onError: (error: unknown) => {
        const serverMessage = (
          error as { response?: { data?: { ERROR?: string } } }
        )?.response?.data?.ERROR;
        toast.error(
          serverMessage?.replace(/^BAD REQUEST exception Error: /, '') ||
            aorText('Could not save the Area of Responsibility.')
        );
      },
    });
    onClose();
  };
  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>
          {aorText(
            `${mode === 'Edit' ? 'Update' : 'Add'} Area of Responsibility (AOR)`
          )}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Name</label>
            <Controller
              control={control}
              name="name"
              rules={{ required: true }}
              render={({ field: { onChange, value } }) => (
                <StylishNewInput
                  placeholder="Enter Name"
                  type="text"
                  onChange={onChange}
                  value={value}
                />
              )}
            />
            {errors?.name && (
              <span className="form-text form-error">
                This field is required
              </span>
            )}
          </div>
          <div className="mb-3">
            <label className="form-label">Description</label>
            <Controller
              control={control}
              name="description"
              render={({ field: { onChange, value } }) => (
                <TiptapRichTextEditor
                  value={value || ''}
                  onChange={onChange}
                  placeholder="Enter description..."
                  minHeight="150px"
                />
              )}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              {aorText('Parent AOR (optional)')}
            </label>
            <Controller
              control={control}
              name="parent_item_id"
              render={({ field: { onChange, value } }) => (
                <StylishNewSelect
                  options={parentOptions}
                  value={
                    parentOptions.find((option) => option.value === value) ??
                    null
                  }
                  onChange={(next: ParentOption | null) =>
                    onChange(next?.value ?? null)
                  }
                  placeholder={aorText('No parent (top-level AOR)')}
                  isClearable
                  isSearchable
                />
              )}
            />
            <span className="form-text">
              {aorText(
                "Reports for this AOR are included as sections in its parent's reports."
              )}
            </span>
          </div>
          <div className="mb-3">
            <label className="form-label">Location:</label>
            <GeoLocationComponent
              location={geoLocation}
              setLocation={setGeoLocation}
              showFemaRegionMenu
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="button-group">
            <StylishNewButton
              type="button"
              className="button--secondary button--reverse"
              onClick={onClose}
            >
              Cancel
            </StylishNewButton>
            <StylishNewButton type="submit" className="button--primary">
              {aorText(
                `${mode === 'Edit' ? 'Update' : 'Add'} Area of Responsibility (AOR)`
              )}
            </StylishNewButton>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
